import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_LABELS = {
  blog:   'Blog Post',
  social: 'Social',
  email:  'Email',
};

const TYPE_COLORS = {
  blog:   '#eff6ff',
  social: '#fce7f3',
  email:  '#f3e8ff',
};

function fmtDate(raw, withTime) {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  } catch { return '—'; }
}

export default function AdminContentQueue() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processing, setProcessing] = useState({});

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/admin/agents/content/queue`, { params: { status: 'pending_review' }, headers }),
        axios.get(`${API}/api/admin/agents/content/queue`, { params: { status: 'approved' }, headers }),
      ]);
      setPending(pRes.data || []);
      setApproved(aRes.data || []);
    } catch (err) {
      console.error('Failed to load content queue:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePiece = async (id, action) => {
    let payload = {};
    if (action === 'approve') {
      // Optional publish date. Cancel aborts; blank approves unscheduled.
      const when = window.prompt('Publish date (YYYY-MM-DD) — leave blank to approve unscheduled:');
      if (when === null) return;
      payload = { scheduled_for: when.trim() || null };
    } else if (action === 'reject') {
      const reason = window.prompt('Reason for rejection (optional):');
      if (reason === null) return;
      payload = { reason };
    }
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await axios.patch(`${API}/api/admin/agents/content/${id}/${action}`, payload, { headers });
      await fetchData();
    } catch (err) {
      console.error(err.message);
      alert(err?.response?.data?.error || err.message || 'Action failed.');
    } finally {
      setProcessing(p => ({ ...p, [id]: false }));
    }
  };

  const tabStyle = (tab) => ({
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: activeTab === tab ? '#3a6ad9' : 'transparent',
    color: activeTab === tab ? '#fff' : '#6b7280',
  });

  const pieces = activeTab === 'pending' ? pending : approved;

  const PieceCard = ({ p }) => {
    const typeBg = TYPE_COLORS[p.content_type] || '#f3f4f6';
    const isPending = p.status === 'pending_review';
    return (
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 12, padding: '18px 22px',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 11, fontWeight: 600, background: typeBg, color: '#374151',
            borderRadius: 6, padding: '3px 10px',
          }}>
            {TYPE_LABELS[p.content_type] || p.content_type}
          </span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            {p.pillar_name}{p.topic ? ` · ${p.topic}` : ''}{p.week_of ? ` · week of ${fmtDate(p.week_of)}` : ''}
          </span>
        </div>

        {p.title && (
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
            {p.title}
          </div>
        )}
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 10 }}>
          {p.body}
        </div>

        {p.meta_description && (
          <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginBottom: 6 }}>
            Meta: {p.meta_description}
          </div>
        )}
        {p.hashtags && (
          <div style={{ fontSize: 12, color: '#3a6ad9', marginBottom: 12 }}>
            {p.hashtags}
          </div>
        )}

        {isPending ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={processing[p.id]}
              onClick={() => handlePiece(p.id, 'approve')}
              style={{
                background: processing[p.id] ? '#9ca3af' : '#3a6ad9', color: '#fff',
                border: 'none', borderRadius: 7, padding: '7px 16px',
                fontSize: 12, fontWeight: 600, cursor: processing[p.id] ? 'default' : 'pointer',
              }}>
              Approve
            </button>
            <button
              disabled={processing[p.id]}
              onClick={() => handlePiece(p.id, 'reject')}
              style={{
                background: '#fff', color: '#374151', border: '1px solid #e5e7eb',
                borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600,
                cursor: processing[p.id] ? 'default' : 'pointer',
              }}>
              Reject
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
            ✓ Approved {fmtDate(p.approved_at, true)}
            {p.scheduled_for ? ` · Scheduled for ${fmtDate(p.scheduled_for)}` : ' · Unscheduled'}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Marketing</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Content Queue</h1>
          </div>
          {pending.length > 0 && (
            <div style={{
              background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, color: '#92400e', fontWeight: 600,
            }}>
              {pending.length} piece{pending.length !== 1 ? 's' : ''} pending review
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 10,
          padding: 4, width: 'fit-content', marginBottom: 20,
        }}>
          <button style={tabStyle('pending')} onClick={() => setActiveTab('pending')}>
            Pending Review
            {pending.length > 0 && (
              <span style={{
                marginLeft: 6, background: '#ff082f', color: '#fff', borderRadius: 999,
                fontSize: 10, fontWeight: 700, padding: '1px 6px',
              }}>
                {pending.length}
              </span>
            )}
          </button>
          <button style={tabStyle('approved')} onClick={() => setActiveTab('approved')}>
            Approved
          </button>
        </div>

        {loading && (
          <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading...</div>
        )}

        {!loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pieces.length === 0 && (
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                {activeTab === 'pending'
                  ? 'No content pending review — the content agent has not queued anything yet'
                  : 'No approved content yet'}
              </div>
            )}
            {pieces.map(p => <PieceCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
